import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Plus, 
  X, 
  Eye, 
  EyeOff, 
  Calendar,
  User,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { format, addDays, addWeeks, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useResourceStore } from '../../store/resourceStore';
import type { Resource, VisibilityAssignment } from '../../types/resources';
import type { User as AppUser } from '../../types';

interface VisibilityManagerProps {
  resource: Resource;
  onClose: () => void;
  onSuccess: () => void;
}

export const VisibilityManager: React.FC<VisibilityManagerProps> = ({
  resource,
  onClose,
  onSuccess
}) => {
  const { visibilities, assignResourceVisibility, removeResourceVisibility, fetchResourceVisibilities } = useResourceStore();
  const [porteurs, setPorteurs] = useState<AppUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [expirationOption, setExpirationOption] = useState<'never' | '1week' | '1month' | '3months' | 'custom'>('never');
  const [customDate, setCustomDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    loadPorteurs();
    fetchResourceVisibilities(resource.id);
  }, [resource.id]);

  const loadPorteurs = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('profile_type', 'PORTEUR')
        .order('full_name');

      if (error) throw error;
      setPorteurs(data || []);
    } catch (error) {
      console.error('Error loading porteurs:', error);
      // Mock data for development
      setPorteurs([
        {
          id: '1',
          full_name: 'Marie Joseph',
          email: 'marie.joseph@test.com',
          profile_type: 'PORTEUR',
          organization: 'Coopérative Agricole Gwada',
          phone: null,
          address: null,
          city: 'Basse-Terre',
          postal_code: null,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        {
          id: '2',
          full_name: 'Jean Baptiste',
          email: 'jean.baptiste@test.com',
          profile_type: 'PORTEUR',
          organization: 'Association Jeunesse Culturelle',
          phone: null,
          address: null,
          city: 'Pointe-à-Pitre',
          postal_code: null,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        {
          id: '3',
          full_name: 'Lucie Moreau',
          email: 'lucie.moreau@test.com',
          profile_type: 'PORTEUR',
          organization: 'Éco-Solidaire 971',
          phone: null,
          address: null,
          city: 'Les Abymes',
          postal_code: null,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPorteurs = porteurs.filter(porteur => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      porteur.full_name?.toLowerCase().includes(searchLower) ||
      porteur.email?.toLowerCase().includes(searchLower) ||
      porteur.organization?.toLowerCase().includes(searchLower)
    );
  });

  const getExpirationDate = () => {
    const now = new Date();
    switch (expirationOption) {
      case '1week':
        return addWeeks(now, 1).toISOString();
      case '1month':
        return addMonths(now, 1).toISOString();
      case '3months':
        return addMonths(now, 3).toISOString();
      case 'custom':
        return customDate ? new Date(customDate).toISOString() : undefined;
      default:
        return undefined;
    }
  };

  const handleAssignVisibility = async () => {
    if (selectedUsers.length === 0) return;

    setIsAssigning(true);
    try {
      const assignment: VisibilityAssignment = {
        resource_id: resource.id,
        user_ids: selectedUsers,
        expires_at: getExpirationDate(),
        notes: notes.trim() || undefined
      };

      await assignResourceVisibility(assignment);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error assigning visibility:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveVisibility = async (userId: string) => {
    try {
      await removeResourceVisibility(resource.id, userId);
    } catch (error) {
      console.error('Error removing visibility:', error);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const isUserVisible = (userId: string) => {
    return visibilities.some(v => v.user_id === userId);
  };

  const getUserVisibility = (userId: string) => {
    return visibilities.find(v => v.user_id === userId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Gérer la visibilité</h2>
            <p className="text-sm text-gray-600 mt-1">{resource.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Visibilities */}
          {visibilities.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Porteurs ayant accès ({visibilities.length})</h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {visibilities.map((visibility) => {
                  const porteur = porteurs.find(p => p.id === visibility.user_id);
                  return (
                    <div key={visibility.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {porteur?.full_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{porteur?.full_name || 'Utilisateur inconnu'}</p>
                          <p className="text-sm text-gray-600">{porteur?.organization}</p>
                          {visibility.expires_at && (
                            <p className="text-xs text-gray-500">
                              Expire le {format(new Date(visibility.expires_at), 'dd/MM/yyyy', { locale: fr })}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveVisibility(visibility.user_id)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Retirer l'accès"
                      >
                        <EyeOff className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add New Visibilities */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Donner accès à de nouveaux porteurs</h3>
            
            {/* Search Porteurs */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher un porteur de projet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Porteurs List */}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredPorteurs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>
                    {searchTerm 
                      ? 'Aucun porteur trouvé avec ces critères'
                      : 'Aucun porteur de projet disponible'
                    }
                  </p>
                </div>
              ) : (
                filteredPorteurs.map((porteur) => {
                  const isSelected = selectedUsers.includes(porteur.id);
                  const hasAccess = isUserVisible(porteur.id);
                  const visibility = getUserVisibility(porteur.id);
                  
                  return (
                    <label
                      key={porteur.id}
                      className={`flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                        hasAccess ? 'bg-green-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUserSelection(porteur.id)}
                        disabled={hasAccess}
                        className="mr-3"
                      />
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {porteur.full_name?.charAt(0) || porteur.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900">
                              {porteur.full_name || porteur.email}
                            </p>
                            {hasAccess && (
                              <div className="flex items-center space-x-1 text-green-600">
                                <Eye className="w-4 h-4" />
                                <span className="text-xs font-medium">A déjà accès</span>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{porteur.organization}</p>
                          <p className="text-xs text-gray-500">{porteur.city}</p>
                          {visibility?.expires_at && (
                            <p className="text-xs text-orange-600">
                              Expire le {format(new Date(visibility.expires_at), 'dd/MM/yyyy', { locale: fr })}
                            </p>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            {selectedUsers.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 mb-3">
                  <strong>{selectedUsers.length}</strong> porteur{selectedUsers.length > 1 ? 's' : ''} sélectionné{selectedUsers.length > 1 ? 's' : ''}
                </p>
                
                {/* Expiration Options */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Durée d'accès
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {[
                        { value: 'never', label: 'Permanent' },
                        { value: '1week', label: '1 semaine' },
                        { value: '1month', label: '1 mois' },
                        { value: '3months', label: '3 mois' },
                        { value: 'custom', label: 'Personnalisé' }
                      ].map((option) => (
                        <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            value={option.value}
                            checked={expirationOption === option.value}
                            onChange={(e) => setExpirationOption(e.target.value as any)}
                            className="text-blue-600"
                          />
                          <span className="text-sm text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {expirationOption === 'custom' && (
                    <div>
                      <label htmlFor="custom-date" className="block text-sm font-medium text-gray-700 mb-2">
                        Date d'expiration
                      </label>
                      <input
                        id="custom-date"
                        type="date"
                        value={customDate}
                        onChange={(e) => setCustomDate(e.target.value)}
                        min={format(new Date(), 'yyyy-MM-dd')}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (optionnel)
                    </label>
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Pourquoi donner accès à cette ressource..."
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {visibilities.length > 0 && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>{visibilities.length} porteur{visibilities.length > 1 ? 's ont' : ' a'} déjà accès</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Fermer
              </button>
              {selectedUsers.length > 0 && (
                <button
                  onClick={handleAssignVisibility}
                  disabled={isAssigning}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isAssigning ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Attribution...</span>
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      <span>Donner accès ({selectedUsers.length})</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};